// PLAN-cockpit-domain-mode Phase 3 (2026-05-07〜):
// Detail Panel に Domain overlay セクションを追加。
//   3a: declared / curated / inferred の read-only 表示
//   3b: curated overlay の include / remove
//   3c: curated exclude + reason 入力 dialog
//
// Vocabulary は useDomainVocabulary で動的取得 (Phase 1 で実装済)。
// sensitive Domain (relation/mind/health) は amber 色で警告表示。

import { useEffect, useState } from 'react'
import { getDomainOverlay, patchDomainOverlay, type DomainOverlay } from '../../api/kdb'
import { UnauthorizedError } from '../../api/client'
import { useDomainVocabulary } from '../../hooks/useDomainVocabulary'
import type { Domain } from '../../types'

const SENSITIVE_DOMAINS: Domain[] = ['relation', 'mind', 'health']

function isSensitive(domain: string): boolean {
  return (SENSITIVE_DOMAINS as string[]).includes(domain)
}

interface Props {
  itemType: 'entry' | 'article'
  itemId: string
  onUnauthorized: () => void
}

export function DomainOverlaySection({ itemType, itemId, onUnauthorized }: Props) {
  const [overlay, setOverlay] = useState<DomainOverlay | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [reload, setReload] = useState(0)
  const [excludeTarget, setExcludeTarget] = useState<string | null>(null)  // Phase 3c: exclude dialog 対象 domain
  const vocab = useDomainVocabulary()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getDomainOverlay(itemType, itemId)
      .then((res) => {
        if (cancelled) return
        setOverlay(res)
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        if (e instanceof UnauthorizedError) {
          onUnauthorized()
          return
        }
        setError(e instanceof Error ? e.message : String(e))
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [itemType, itemId, onUnauthorized, reload])

  async function addCuratedInclude(domain: string) {
    setAdding(true)
    try {
      await patchDomainOverlay(itemType, itemId, {
        set: [{ domain, polarity: 'include', source: 'cockpit' }],
      })
      setReload((n) => n + 1)
    } catch (e: unknown) {
      setError(`add 失敗: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setAdding(false)
    }
  }

  async function addCuratedExclude(domain: string, reason: string) {
    setAdding(true)
    try {
      await patchDomainOverlay(itemType, itemId, {
        set: [{ domain, polarity: 'exclude', reason, source: 'cockpit' }],
      })
      setExcludeTarget(null)
      setReload((n) => n + 1)
    } catch (e: unknown) {
      setError(`exclude 失敗: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setAdding(false)
    }
  }

  async function removeCurated(domain: string, polarity: 'include' | 'exclude') {
    if (!confirm(`curated ${polarity}: ${domain} を削除しますか?`)) return
    setAdding(true)
    try {
      await patchDomainOverlay(itemType, itemId, {
        remove: [{ domain, polarity }],
      })
      setReload((n) => n + 1)
    } catch (e: unknown) {
      setError(`remove 失敗: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setAdding(false)
    }
  }

  if (loading) return null  // detail panel 内では読み込み中は静かに省略

  if (error) {
    return (
      <section className="border border-slate-200 rounded p-3 mt-4">
        <h3 className="text-xs font-semibold text-slate-700 mb-1">Domain</h3>
        <p className="text-xs text-red-600">取得失敗: {error}</p>
      </section>
    )
  }

  if (!overlay) return null

  // curated set (UI で「追加候補」を絞り込むのに使う)
  const curatedDomains = new Set(
    overlay.curated_domains.filter((c) => c.polarity === 'include').map((c) => c.domain)
  )
  const declaredSet = new Set(overlay.declared_domains)
  // 追加候補: declared にも curated include にもない vocabulary 値
  const addCandidates = vocab.values.filter(
    (v) => !curatedDomains.has(v.value) && !declaredSet.has(v.value)
  )

  return (
    <section className="border border-slate-200 rounded p-3 mt-4 space-y-2">
      <h3 className="text-xs font-semibold text-slate-700">Domain</h3>

      {/* declared (immutable、entry は P1) */}
      {overlay.declared_domains.length > 0 && (
        <div className="text-xs">
          <span className="text-slate-500 mr-1">declared:</span>
          {overlay.declared_domains.map((d) => (
            <span
              key={d}
              className={`inline-block px-1.5 py-0.5 rounded mr-1 ${
                isSensitive(d) ? 'bg-amber-100 text-amber-900' : 'bg-slate-200 text-slate-800'
              }`}
              title={isSensitive(d) ? `Sensitive Domain (${d})` : d}
            >
              {d}
            </span>
          ))}
        </div>
      )}

      {/* curated (include / exclude、編集可) */}
      {overlay.curated_domains.length > 0 && (
        <div className="text-xs">
          <span className="text-slate-500 mr-1">curated:</span>
          {overlay.curated_domains.map((c) => (
            <span
              key={`${c.domain}-${c.polarity}`}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded mr-1 ${
                c.polarity === 'exclude'
                  ? 'bg-red-100 text-red-900'
                  : isSensitive(c.domain)
                    ? 'bg-amber-100 text-amber-900'
                    : 'bg-emerald-100 text-emerald-900'
              }`}
              title={c.reason ? `reason: ${c.reason}` : `${c.polarity}`}
            >
              {c.polarity === 'exclude' ? '−' : '+'}{c.domain}
              <button
                type="button"
                onClick={() => removeCurated(c.domain, c.polarity)}
                disabled={adding}
                className="text-[10px] text-slate-500 hover:text-red-600 disabled:opacity-50"
                aria-label="削除"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* inferred (Plan B classifier 由来、read-only) */}
      {overlay.inferred_domains.length > 0 && (
        <div className="text-xs">
          <span className="text-slate-500 mr-1">inferred:</span>
          {overlay.inferred_domains.map((i) => (
            <span
              key={i.domain}
              className={`inline-block px-1.5 py-0.5 rounded mr-1 italic ${
                isSensitive(i.domain) ? 'bg-amber-50 text-amber-700' : 'bg-sky-50 text-sky-700'
              }`}
              title={`confidence: ${i.confidence ?? '?'} / source: ${i.source ?? '?'}`}
            >
              {i.domain}
              {i.confidence != null && (
                <span className="ml-1 text-[10px] opacity-60">{i.confidence.toFixed(2)}</span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* 追加 UI: curated include の追加 + exclude (reason 入力 dialog) */}
      {addCandidates.length > 0 && (
        <div className="text-xs pt-1 border-t border-slate-100 space-y-1">
          <div>
            <span className="text-slate-500 mr-1">追加 (include):</span>
            {addCandidates.map((v) => (
              <button
                key={`inc-${v.value}`}
                type="button"
                onClick={() => addCuratedInclude(v.value)}
                disabled={adding}
                className={`inline-block px-1.5 py-0.5 rounded mr-1 disabled:opacity-50 ${
                  isSensitive(v.value)
                    ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title={isSensitive(v.value) ? `Sensitive Domain (${v.value}) を curated include` : `${v.value} を curated include`}
              >
                + {v.label}
              </button>
            ))}
          </div>
          <div>
            <span className="text-slate-500 mr-1">除外 (exclude):</span>
            {vocab.values
              .filter((v) =>
                !overlay.curated_domains.some((c) => c.domain === v.value && c.polarity === 'exclude')
              )
              .map((v) => (
                <button
                  key={`exc-${v.value}`}
                  type="button"
                  onClick={() => setExcludeTarget(v.value)}
                  disabled={adding}
                  className="inline-block px-1.5 py-0.5 rounded mr-1 disabled:opacity-50 bg-red-50 text-red-700 hover:bg-red-100"
                  title={`${v.value} を curated exclude (reason 必須)`}
                >
                  − {v.label}
                </button>
              ))}
          </div>
        </div>
      )}

      {overlay.declared_domains.length === 0 &&
        overlay.curated_domains.length === 0 &&
        overlay.inferred_domains.length === 0 && (
          <p className="text-xs text-slate-400 italic">Domain は未分類です</p>
        )}

      {/* Phase 3c: exclude reason 入力 dialog */}
      {excludeTarget && (
        <ExcludeReasonDialog
          domain={excludeTarget}
          isSensitive={isSensitive(excludeTarget)}
          onCancel={() => setExcludeTarget(null)}
          onConfirm={(reason) => addCuratedExclude(excludeTarget, reason)}
          submitting={adding}
        />
      )}
    </section>
  )
}

interface ExcludeDialogProps {
  domain: string
  isSensitive: boolean
  onCancel: () => void
  onConfirm: (reason: string) => void
  submitting: boolean
}

function ExcludeReasonDialog({ domain, isSensitive, onCancel, onConfirm, submitting }: ExcludeDialogProps) {
  const [reason, setReason] = useState('')
  const trimmed = reason.trim()
  const canSubmit = trimmed.length > 0 && !submitting

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-800">
            curated exclude — {domain}
            {isSensitive && (
              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">
                Sensitive
              </span>
            )}
          </h3>
          <p className="text-[11px] text-slate-500 mt-1">
            この item を {domain} から明示的に除外します。reason は監査記録 (data-model.md item_domains.reason) として保存されます。
          </p>
        </div>
        <div className="p-4 space-y-3">
          <label className="block text-xs text-slate-700 font-medium">理由 (必須)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="例: 自動推定が誤分類 / この記録は finance ではなく general として扱う"
            rows={3}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-slate-500"
          />
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1 px-3 py-2 text-sm text-slate-700 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={() => onConfirm(trimmed)}
              disabled={!canSubmit}
              className="flex-1 px-3 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? '送信中…' : 'exclude を確定'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
